import React, { useState, useEffect } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const ImageLightbox = ({ 
  isOpen, 
  images, 
  currentIndex, 
  onClose, 
  onNext, 
  onPrevious 
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [showInfo, setShowInfo] = useState(true);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsZoomed(false);
      setShowInfo(true);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e?.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose, onNext, onPrevious]);

  if (!isOpen || !images?.[currentIndex]) return null;

  const currentImage = images?.[currentIndex];

  const handleShare = (platform) => {
    const url = window.location?.href;
    const text = `Check out this amazing view from Dandeli Adventure Resorts: ${currentImage?.title}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      instagram: url // Instagram doesn't support direct sharing via URL
    };

    if (shareUrls?.[platform]) {
      window.open(shareUrls?.[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm">
      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-white text-sm font-mono">
              {currentIndex + 1} / {images?.length}
            </span>
            <span className="text-white/60 text-sm">•</span>
            <span className="text-white/80 text-sm">{currentImage?.category}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
              title="Toggle info"
            >
              <Icon name="Info" size={20} />
            </button>
            
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
              title={isZoomed ? "Zoom out" : "Zoom in"}
            >
              <Icon name={isZoomed ? "ZoomOut" : "ZoomIn"} size={20} />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
              title="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </div>
      {/* Navigation Buttons */}
      <button
        onClick={onPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/30 text-white hover:bg-black/50 transition-micro"
        disabled={currentIndex === 0}
        title="Previous image"
      >
        <Icon name="ChevronLeft" size={24} />
      </button>
      <button
        onClick={onNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/30 text-white hover:bg-black/50 transition-micro"
        disabled={currentIndex === images?.length - 1}
        title="Next image"
      >
        <Icon name="ChevronRight" size={24} />
      </button>
      {/* Main Image */}
      <div className="flex items-center justify-center h-full p-4 pt-20 pb-32">
        <div className={`relative max-w-full max-h-full transition-transform duration-300 ${
          isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
        }`}>
          <Image
            src={currentImage?.src}
            alt={currentImage?.alt}
            className="max-w-full max-h-full object-contain rounded-lg shadow-organic-xl"
            onClick={() => setIsZoomed(!isZoomed)}
          />
        </div>
      </div>
      {/* Bottom Info Panel */}
      {showInfo && (
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between space-y-4 md:space-y-0">
              <div>
                <h2 className="text-white text-xl font-heading font-semibold mb-2">
                  {currentImage?.title}
                </h2>
                {currentImage?.location && (
                  <p className="text-white/80 text-sm flex items-center mb-2">
                    <Icon name="MapPin" size={14} className="mr-2" />
                    {currentImage?.location}
                  </p>
                )}
                {currentImage?.description && (
                  <p className="text-white/70 text-sm max-w-2xl">
                    {currentImage?.description}
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-4">
                {/* Share Buttons */}
                <div className="flex items-center space-x-2">
                  <span className="text-white/60 text-sm mr-2">Share:</span>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
                    title="Share on Facebook"
                  >
                    <Icon name="Facebook" size={16} />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
                    title="Share on Twitter"
                  >
                    <Icon name="Twitter" size={16} />
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-micro"
                    title="Share on WhatsApp"
                  >
                    <Icon name="MessageCircle" size={16} />
                  </button>
                </div>

                {/* Book Now CTA */}
                <Button
                  variant="default"
                  size="sm"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => window.location.href = '/booking-engine'}
                  className="bg-primary hover:bg-primary/90"
                >
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;